import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Autocomplete,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment'; // Added InputAdornment
import NavigationDrawer from '../Navigation/NavigationDrawer';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { useSharePoint } from '../../contexts/SharePointContext';
import FolderBrowserDialog from './FolderBrowserDialog';
import authApi from '../../services/authApi';
import { useUserSession } from '../../contexts/UserContext';

const SecurityLookup = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { userSession } = useUserSession();
  const { effectiveConfigId, configurations, refreshTrigger } = useSharePoint();

  // View mode state
  const [viewMode, setViewMode] = useState('standard'); // 'standard' | 'comparison'

  // Data states
  const [libraries, setLibraries] = useState([]);
  const [users, setUsers] = useState([]);
  const [permissionData, setPermissionData] = useState([]);
  
  // Filter states
  const [selectedLibraries, setSelectedLibraries] = useState([]);
  const [folderFilter, setFolderFilter] = useState('');
  const [filenameFilter, setFilenameFilter] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Comparison specific states
  const [userA, setUserA] = useState(null);
  const [userB, setUserB] = useState(null);
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonTab, setComparisonTab] = useState(0);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [loadingLibraries, setLoadingLibraries] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(''); // Add warning state for large result sets
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFolderBrowserOpen, setIsFolderBrowserOpen] = useState(false); // State for dialog

  // Update the state to include totalItems for pagination
  const [totalItems, setTotalItems] = useState(0);

  // Permission level colors
  const permissionColors = {
    'Read': theme.palette.info.main,
    'Edit': theme.palette.warning.main,
    'Contribute': theme.palette.warning.main,
    'Full Control': theme.palette.error.main,
    'Owner': theme.palette.error.main,
    'SP.Full Control': theme.palette.error.main,
    'SP.Edit': theme.palette.warning.main,
    'DEFAULT': theme.palette.text.secondary
  };
  
  useEffect(() => {
    // Reset states when the configuration changes
    setSelectedLibraries([]);
    setFolderFilter('');
    setFilenameFilter('');
    setSelectedUsers([]);
    setSearchPerformed(false);
    setPermissionData([]);
    setComparisonData([]);
    setUserA(null);
    setUserB(null);
    setComparisonTab(0);
    setPage(0);
    
    // Load libraries and users when component mounts or configuration changes
    if (userSession?.email && effectiveConfigId) {
      fetchLibraries();
      fetchUsers();
    }
  }, [userSession?.email, effectiveConfigId, refreshTrigger]);

  const fetchLibraries = async () => {
    try {
      setLoadingLibraries(true);
      const token = authApi.getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/sharepoint/document-libraries`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            configId: effectiveConfigId
          }
        }
      );

      const transformedLibraries = (response.data || []).map((lib) => ({
        library_id: lib.internal_id || lib.id,
        library_name: lib.name,
        library_url: lib.url,
        graph_id: lib.id,
        has_internal_id: !!lib.internal_id
      })).filter((lib) => lib.library_id);

      const filteredOut = (response.data || []).filter((lib) => !lib.internal_id);
      if (filteredOut.length > 0) {
        console.warn(
          'Libraries missing internal_id (ETL may be incomplete) for config',
          effectiveConfigId,
          filteredOut
        );
      }
      
      setLibraries(transformedLibraries);
      
      // Show warning if no libraries are available
      if (transformedLibraries.length === 0) {
        setError('No document libraries are available. This may be due to incomplete data synchronization. Please contact your administrator.');
      } else if (filteredOut.length > 0) {
        // Show warning if some libraries are using Graph IDs instead of internal_ids
        setError(`Warning: ${filteredOut.length} libraries are using temporary IDs due to incomplete data synchronization. Some features may not work correctly. Please contact your administrator to run the ETL process.`);
      }
    } catch (error) {
      console.error('Error fetching libraries:', error);
      setError('Failed to load document libraries');
    } finally {
      setLoadingLibraries(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = authApi.getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/datawarehouse/users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            configId: effectiveConfigId
          }
        }
      );
      if (response.data && response.data.success) {
        setUsers(response.data.data);
      } else {
        console.error('Failed to fetch users data:', response.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSearch = async () => {
    if (selectedLibraries.length === 0) {
      setError('Please select at least one document library');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setPage(0);

      const userNamesToSend = selectedUsers.map(user => user.display_name);
      const libraryIds = selectedLibraries.map((lib) => lib.library_id);

      if (libraryIds.some(id => !id)) {
        console.error('Some library IDs are missing:', libraryIds);
        setError('Invalid library selection - some libraries are not properly configured. Please refresh the page and try again.');
        return;
      }
      
      const token = authApi.getToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/datawarehouse/security-lookup`,
        {
          libraryIds: libraryIds,
          folderPath: folderFilter,
          filename: filenameFilter,
          userIds: userNamesToSend,
          configId: effectiveConfigId
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );

      if (response.data && response.data.success) {
        if (response.data.data.length === 0) {
          console.warn('API returned successful response but with zero results');
        }
        setPermissionData(response.data.data);
        setSearchPerformed(true);
      } else {
        setError(response.data?.message || 'Failed to fetch permission data');
      }
    } catch (error) {
      console.error('Error during search:', error);
      setError(error.response?.data?.message || 'Failed to fetch permission data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!permissionData || permissionData.length === 0) return;

    // Create new workbook using ExcelJS
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Permissions');

    // Define columns with specific widths
    worksheet.columns = [
      { key: 'itemName', width: 30 },
      { key: 'itemType', width: 15 },
      { key: 'itemPath', width: 60 },
      { key: 'userGroup', width: 30 },
      { key: 'permission', width: 20 },
      { key: 'inherited', width: 15 },
      { key: 'container', width: 25 },
      { key: 'url', width: 60 }
    ];

    // Add report title to row 1
    const titleRow = worksheet.getRow(1);
    titleRow.getCell(1).value = 'Vertilocity SecurePoint IQ Security Lookup Report - Report Generated: ' + new Date().toLocaleString();
    worksheet.mergeCells('A1:H1');
    titleRow.height = 35;
    titleRow.font = {
      bold: true,
      size: 16,
      color: { argb: 'FFF57C00' }
    };
    titleRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFAFAFA' }
    };
    titleRow.alignment = {
      horizontal: 'center',
      vertical: 'middle'
    };

    // Add column headers to row 2
    const headerRow = worksheet.getRow(2);
    headerRow.values = ['Item Name', 'Item Type', 'Item Path', 'User/Group', 'Permission', 'Inherited', 'Container', 'URL'];
    headerRow.height = 28;
    headerRow.font = {
      bold: true,
      size: 12,
      color: { argb: '000000' }
    };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF5F5F5' }
    };
    headerRow.eachCell((cell) => {
      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle'
      };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // Set row 3 and beyond for data rows
    let rowIndex = 3;
    permissionData.forEach((item, index) => {
      const row = worksheet.getRow(rowIndex++);
      
      row.values = [
        item.itemName,
        item.item_type,
        item.item_path,
        item.display_name,
        item.permission_level,
        item.is_inherited ? 'Yes' : 'No',
        item.container_name,
        item.item_url
      ];

      row.height = 25;
      row.alignment = { vertical: 'middle' };

      // Alternate row colors
      row.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: index % 2 === 0 ? 'FFFFFFFF' : 'FFFAFAFA' }
      };

      // Add cell borders and alignment
      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFEEEEEE' } },
          bottom: { style: 'thin', color: { argb: 'FFEEEEEE' } },
          left: { style: 'thin', color: { argb: 'FFEEEEEE' } },
          right: { style: 'thin', color: { argb: 'FFEEEEEE' } }
        };
        
        // Center specific columns
        if (colNumber === 2 || colNumber === 5 || colNumber === 6) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        }
      });

      // Apply hyperlink to item name if URL exists
      if (item.item_url) {
        const cell = row.getCell(1);
        cell.value = {
          text: item.itemName,
          hyperlink: item.item_url
        };
        cell.font = {
          color: { argb: '0563C1' },
          underline: true
        };
      }

      // Highlight permission cell based on level
      const permissionCell = row.getCell(5);
      const level = item.permission_level;
      
      if (level === 'Full Control' || level === 'Owner' || level === 'SP.Full Control') {
        permissionCell.font = { color: { argb: 'FFFF0000' }, bold: true }; // Red
      } else if (level === 'Edit' || level === 'Contribute' || level === 'SP.Edit') {
        permissionCell.font = { color: { argb: 'FFFF9900' }, bold: true }; // Orange
      } else if (level === 'Read') {
        permissionCell.font = { color: { argb: '0000FF' } }; // Blue
      }
    });

    // Apply frozen panes for header rows
    worksheet.views = [
      {
        state: 'frozen',
        xSplit: 0,
        ySplit: 2,
        topLeftCell: 'A3',
        activeCell: 'A3'
      }
    ];

    // Apply autoFilter
    worksheet.autoFilter = {
      from: { row: 2, column: 1 },
      to: { row: 2 + permissionData.length, column: 8 }
    };

    // Generate Excel file and save
    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      saveAs(blob, `security-lookup-${new Date().toISOString().slice(0, 10)}.xlsx`);
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    
    // Only refresh data in comparison mode
    if (viewMode === 'comparison' && comparisonData.length > 0) {
      setComparisonLoading(true);
      
      // Call refreshComparisonData with the current tab and explicitly pass the new page number
      refreshComparisonData(comparisonTab, newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    
    // Only refresh data in comparison mode
    if (viewMode === 'comparison' && comparisonData.length > 0) {
      setComparisonLoading(true);
      
      // Call refreshComparisonData with the current tab and explicitly pass page 0
      refreshComparisonData(comparisonTab, 0);
    }
  };

  const getPermissionColor = (permissionLevel) => {
    // Get the base permission without any prefix
    const basePermission = stripPermissionPrefix(permissionLevel);
    return permissionColors[basePermission] || theme.palette.grey[500];
  };
  
  // Helper function to strip permission prefixes for display
  const stripPermissionPrefix = (permission) => {
    if (!permission) return '';
    
    // Remove prefixes like [REMOVED], [ADDED], [RAISED], [LOWERED]
    const prefixes = ['[REMOVED]', '[ADDED]', '[RAISED]', '[LOWERED]', 'Removed-', 'Added-', 'Raised-', 'Lowered-'];
    
    let result = permission;
    prefixes.forEach(prefix => {
      if (result.startsWith(prefix)) {
        result = result.substring(prefix.length);
      }
    });
    
    return result;
  };
  
  // Filter the permissionData for display based on pagination
  const displayedPermissions = searchPerformed ? 
    permissionData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : 
    [];

  const groupPermissionsByItem = () => {
    const groupedData = {};
    
    permissionData.forEach(item => {
      const key = `${item.item_id}-${item.item_path}-${item.itemName}`;
      
      if (!groupedData[key]) {
        groupedData[key] = {
          itemInfo: {
            id: item.item_id,
            name: item.itemName,
            type: item.item_type,
            path: item.item_path,
            url: item.item_url,
            container: item.container_name
          },
          permissions: []
        };
      }
      
      // Check if this permission already exists for this user on this item
      const permissionExists = groupedData[key].permissions.some(
        existingPerm => existingPerm.user === item.display_name && 
                        stripPermissionPrefix(existingPerm.permission) === stripPermissionPrefix(item.permission_level)
      );
      
      // Only add if it doesn't already exist
      if (!permissionExists) {
        groupedData[key].permissions.push({
          user: item.display_name,
          permission: item.permission_level,
          inherited: item.is_inherited
        });
      }
    });
    
    return Object.values(groupedData);
  };
  
  const groupedPermissions = groupPermissionsByItem();
  const displayedGroupedPermissions = searchPerformed ? 
    groupedPermissions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : 
    [];

  // Handle view mode change
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      setPage(0);
      setError(null);
      
      // Reset view-specific state when switching modes
      if (newMode === 'comparison') {
        setSelectedUsers([]);
        setComparisonData([]);
      }
    }
  };

  // Improve the comparison tab change handler to ensure proper filtering and prevent UI disappearance
  const handleComparisonTabChange = (event, newValue) => {
    console.log(`Tab changed from ${comparisonTab} to ${newValue}`);
    
    // If clicking the same tab, no need to change anything
    if (comparisonTab === newValue) {
      return;
    }
    
    // Update the tab value
    setComparisonTab(newValue);
    
    // Reset to page 1 whenever we change tabs
    setPage(0);
    
    // Clear any previous errors
    setError(null);
    
    // Refresh the data with the new filter and explicitly pass page 0
    refreshComparisonData(newValue, 0);
  };

  const refreshComparisonData = (filterType = comparisonTab, pageNumber = page) => {
    if (!userA || !userB) {
      console.log('Cannot refresh comparison data: userA or userB is not selected');
      return;
    }
    
    console.log(`Refreshing comparison data with filter type ${filterType}, page: ${pageNumber + 1}`);
    setComparisonLoading(true);
    
    // Get the selected library IDs
    // When using Autocomplete with multiple=true, selectedLibraries is an array of library objects
    const selectedLibraryIds = selectedLibraries.map(lib => lib.library_id);
    
    console.log(`Selected libraries: ${selectedLibraryIds.join(', ')}`);
    console.log('Using config ID:', effectiveConfigId);
    
    // Ensure we have libraries selected
    if (selectedLibraryIds.length === 0) {
      setWarning('Please select at least one library');
      setComparisonLoading(false);
      setComparisonData([]);
      return;
    }

    // Make the API request with auth token
    const token = authApi.getToken();
    axios
      .post(
          `${process.env.REACT_APP_API_URL}/api/datawarehouse/security-comparison`,
          {
            libraryIds: selectedLibraryIds,
            userA: userA.display_name,
            userB: userB.display_name,
            folderPath: folderFilter,
            filenameFilter: filenameFilter,
            page: pageNumber + 1, // Use explicit pageNumber parameter instead of state
            pageSize: rowsPerPage,
            filterType: filterType,
            configId: effectiveConfigId
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          }
        )
      .then(response => {
        console.log('Comparison data received:', response.data);
        if (response.data && response.data.success) {
          const { data, pagination } = response.data;
          
          // Update state with received data
          setComparisonData(data || []);
          setTotalItems(pagination.totalItems || 0);
          
          // Set warning for large datasets
          if (pagination.totalItems > 10000) {
            setWarning('Large result set detected. Consider using more specific filters for better performance.');
          } else {
            setWarning(null);
          }
          
          console.log(`Found ${pagination.totalItems} total items`);
        } else {
          console.error('API call succeeded but returned no data');
          setComparisonData([]);
          setError('No data returned from server');
        }
      })
      .catch(err => {
        console.error('Error fetching comparison data:', err);
        setError(`Error fetching data: ${err.message}`);
        setComparisonData([]);
      })
      .finally(() => {
        setComparisonLoading(false);
      });
  };

  // Update the handleComparisonSearch to use the new refresh function
  const handleComparisonSearch = async () => {
    try {
      // Validate required fields first
      if (!userA || !userB) {
        setError('Please select both users or groups for comparison');
        return;
      }
      
      // Check selected libraries
      if (!selectedLibraries || selectedLibraries.length === 0) {
        setError('Please select at least one document library');
        return;
      }

      // Reset state before search
      setComparisonLoading(true);
      setError(null);
      setPage(0); // Reset to first page
      setComparisonTab(0); // Reset to "All Items" tab
      
      // Call the refresh function directly
      refreshComparisonData(0);
    } catch (error) {
      console.error('Error initiating comparison search:', error);
      setError(`Search error: ${error.message}`);
      setComparisonLoading(false);
    }
  };

  const handleComparisonExport = () => {
    if (!comparisonData || comparisonData.length === 0) return;

    // Create new workbook using ExcelJS
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Comparison');

    // Define columns with specific widths
    worksheet.columns = [
      { key: 'itemName', width: 30 },
      { key: 'itemType', width: 15 },
      { key: 'itemPath', width: 60 },
      { key: 'userA', width: 30 },
      { key: 'userB', width: 30 },
      { key: 'permissionA', width: 20 },
      { key: 'permissionB', width: 20 },
      { key: 'inheritedA', width: 15 },
      { key: 'inheritedB', width: 15 },
      { key: 'container', width: 25 },
      { key: 'url', width: 60 }
    ];

    // Add report title to row 1
    const titleRow = worksheet.getRow(1);
    titleRow.getCell(1).value = 'Vertilocity SecurePoint IQ Security Comparison Report - Report Generated: ' + new Date().toLocaleString();
    worksheet.mergeCells('A1:L1');
    titleRow.height = 35;
    titleRow.font = {
      bold: true,
      size: 16,
      color: { argb: 'FFF57C00' }
    };
    titleRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFAFAFA' }
    };
    titleRow.alignment = {
      horizontal: 'center',
      vertical: 'middle'
    };

    // Add column headers to row 2
    const headerRow = worksheet.getRow(2);
    headerRow.values = ['Item Name', 'Item Type', 'Item Path', 'User A', 'User B', 'Permission A', 'Permission B', 'Inherited A', 'Inherited B', 'Container', 'URL'];
    headerRow.height = 28;
    headerRow.font = {
      bold: true,
      size: 12,
      color: { argb: '000000' }
    };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF5F5F5' }
    };
    headerRow.eachCell((cell) => {
      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle'
      };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // Set row 3 and beyond for data rows
    let rowIndex = 3;
    comparisonData.forEach((item, index) => {
      const row = worksheet.getRow(rowIndex++);
      
      row.values = [
        item.itemName,
        item.item_type,
        item.item_path,
        item.userA,
        item.userB,
        item.permissionA,
        item.permissionB,
        item.is_inheritedA ? 'Yes' : 'No',
        item.is_inheritedB ? 'Yes' : 'No',
        item.container_name,
        item.item_url
      ];

      row.height = 25;
      row.alignment = { vertical: 'middle' };

      // Alternate row colors
      row.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: index % 2 === 0 ? 'FFFFFFFF' : 'FFFAFAFA' }
      };

      // Add cell borders and alignment
      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFEEEEEE' } },
          bottom: { style: 'thin', color: { argb: 'FFEEEEEE' } },
          left: { style: 'thin', color: { argb: 'FFEEEEEE' } },
          right: { style: 'thin', color: { argb: 'FFEEEEEE' } }
        };
        
        // Center specific columns
        if (colNumber === 2 || colNumber === 5 || colNumber === 6 || colNumber === 7 || colNumber === 8) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        }
      });

      // Apply hyperlink to item name if URL exists
      if (item.item_url) {
        const cell = row.getCell(1);
        cell.value = {
          text: item.itemName,
          hyperlink: item.item_url
        };
        cell.font = {
          color: { argb: '0563C1' },
          underline: true
        };
      }

      // Highlight permission cells based on level
      const permissionCellA = row.getCell(5);
      const permissionCellB = row.getCell(6);
      const levelA = item.permissionA;
      const levelB = item.permissionB;
      
      if (levelA === 'Full Control' || levelA === 'Owner' || levelA === 'SP.Full Control') {
        permissionCellA.font = { color: { argb: 'FFFF0000' }, bold: true }; // Red
      } else if (levelA === 'Edit' || levelA === 'Contribute' || levelA === 'SP.Edit') {
        permissionCellA.font = { color: { argb: 'FFFF9900' }, bold: true }; // Orange
      } else if (levelA === 'Read') {
        permissionCellA.font = { color: { argb: '0000FF' } }; // Blue
      }
      
      if (levelB === 'Full Control' || levelB === 'Owner' || levelB === 'SP.Full Control') {
        permissionCellB.font = { color: { argb: 'FFFF0000' }, bold: true }; // Red
      } else if (levelB === 'Edit' || levelB === 'Contribute' || levelB === 'SP.Edit') {
        permissionCellB.font = { color: { argb: 'FFFF9900' }, bold: true }; // Orange
      } else if (levelB === 'Read') {
        permissionCellB.font = { color: { argb: '0000FF' } }; // Blue
      }
    });

    // Apply frozen panes for header rows
    worksheet.views = [
      {
        state: 'frozen',
        xSplit: 0,
        ySplit: 2,
        topLeftCell: 'A3',
        activeCell: 'A3'
      }
    ];

    // Apply autoFilter
    worksheet.autoFilter = {
      from: { row: 2, column: 1 },
      to: { row: 2 + comparisonData.length, column: 11 }
    };

    // Generate Excel file and save
    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      saveAs(blob, `security-comparison-${new Date().toISOString().slice(0, 10)}.xlsx`);
    });
  };

  // Add these helper functions for comparison
  const getComparisonType = (permissionA, permissionB) => {
    if (!permissionA && !permissionB) return 'none';
    if (permissionA && !permissionB) return 'uniqueA';
    if (!permissionA && permissionB) return 'uniqueB';
    if (stripPermissionPrefix(permissionA) === stripPermissionPrefix(permissionB)) return 'same';
    return 'different';
  };

  const getComparisonRowColor = (permissionA, permissionB) => {
    const comparisonType = getComparisonType(permissionA, permissionB);
    switch (comparisonType) {
      case 'uniqueA':
        return 'rgba(3, 169, 244, 0.1)'; // Light blue
      case 'uniqueB':
        return 'rgba(156, 39, 176, 0.1)'; // Light purple
      case 'different':
        return 'rgba(255, 152, 0, 0.1)'; // Light orange
      case 'same':
        return 'rgba(76, 175, 80, 0.1)'; // Light green
      default:
        return 'transparent';
    }
  };

  const getPermissionDifference = (permissionA, permissionB) => {
    if (!permissionA && !permissionB) return 'None';
    if (permissionA && !permissionB) return 'Only User A has access';
    if (!permissionA && permissionB) return 'Only User B has access';
    
    const baseA = stripPermissionPrefix(permissionA);
    const baseB = stripPermissionPrefix(permissionB);
    
    if (baseA === baseB) return 'Same';
    
    // Determine which permission is higher
    const permissionLevels = {
      'Read': 1,
      'Contribute': 2,
      'Edit': 3,
      'Full Control': 4,
      'Owner': 5
    };
    
    const levelA = permissionLevels[baseA] || 0;
    const levelB = permissionLevels[baseB] || 0;
    
    if (levelA > levelB) {
      return `User A has higher access (${baseA} > ${baseB})`;
    } else if (levelB > levelA) {
      return `User B has higher access (${baseB} > ${baseA})`;
    } else {
      return 'Different but equivalent permissions';
    }
  };

  // Filter comparison data based on the active tab
  const getFilteredComparisonData = () => {
    if (!comparisonData || comparisonData.length === 0) return [];
    
    switch (comparisonTab) {
      case 0: // All Items
        return comparisonData;
      case 1: // Shared Permissions - same permission level
        return comparisonData.filter(item => 
          item.permissionA && 
          item.permissionB && 
          stripPermissionPrefix(item.permissionA) === stripPermissionPrefix(item.permissionB)
        );
      case 2: // Unique to User A
        return comparisonData.filter(item => 
          item.permissionA && !item.permissionB
        );
      case 3: // Unique to User B
        return comparisonData.filter(item => 
          !item.permissionA && item.permissionB
        );
      case 4: // Different Permission Levels
        return comparisonData.filter(item => 
          item.permissionA && 
          item.permissionB && 
          stripPermissionPrefix(item.permissionA) !== stripPermissionPrefix(item.permissionB)
        );
      default:
        return comparisonData;
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
      <NavigationDrawer
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection={''}
        setActiveSection={() => {}}
        navigate={navigate}
      />
      
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Security Lookup
          </Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="security lookup view mode"
            size="small"
          >
            <ToggleButton value="standard">Standard View</ToggleButton>
            <ToggleButton value="comparison">Comparison View</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'rgba(244, 81, 30, 0.06)' }}>
          {viewMode === 'standard' && (
            <>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Security Lookup — Standard view
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                Search permissions on items in the libraries you pick. Results show <strong>who has access</strong> to
                files and folders (and at what level), using data synced to the warehouse. Use filters to narrow by
                path, file name, user, or group before you search or export.
              </Typography>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Tips
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
                <Typography component="li" variant="body2">
                  Choose the correct <strong>site</strong> in the header, then one or more <strong>document libraries</strong>.
                </Typography>
                <Typography component="li" variant="body2">
                  Use <strong>Export</strong> to download the current result set to Excel for sharing or archiving.
                </Typography>
              </Box>
            </>
          )}
          {viewMode === 'comparison' && (
            <>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Security Lookup — Comparison view
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                Compare <strong>two users or groups side by side</strong> on the same libraries. The grid highlights
                where their effective access matches, differs, or exists for only one party—useful for access reviews,
                role changes, and finding overly broad grants.
              </Typography>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Tips
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
                <Typography component="li" variant="body2">
                  Select libraries and <strong>both</strong> identities, set optional folder or file filters, then run the
                  comparison. Sub-tabs (for example All Items vs. differences) focus the list.
                </Typography>
                <Typography component="li" variant="body2">
                  Row coloring calls out comparison outcomes; use <strong>Export</strong> to capture the analysis.
                </Typography>
              </Box>
            </>
          )}
        </Paper>
        
        {/* Debug Configuration Info */}
        {process.env.NODE_ENV === 'development' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Debug Info:</strong><br/>
            Selected Config ID: {effectiveConfigId}<br/>
            Available Configs: {configurations.map(c => `${c.id} (${c.tenant_url})`).join(', ')}
          </Alert>
        )}
        
        {viewMode === 'standard' && (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                <FilterListIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Filters
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    multiple
                    options={libraries}
                    getOptionLabel={(option) => option.library_name}
                    value={selectedLibraries}
                    onChange={(event, newValue) => {
                      setSelectedLibraries(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Document Libraries"
                        placeholder="Select document libraries"
                        variant="outlined"
                        disabled={loadingLibraries}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: loadingLibraries ? (
                            <CircularProgress size={20} />
                          ) : (
                            <>
                              <FolderIcon sx={{ color: 'action.active', mr: 1 }} />
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.library_id}>
                        <FolderIcon sx={{ color: 'primary.main', mr: 1 }} />
                        {option.library_name}
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const tagProps = getTagProps({ index });
                        // Remove key from props that will be spread
                        const { key, ...chipProps } = tagProps;
                        return (
                          <Chip
                            key={option.library_id}
                            label={option.library_name}
                            {...chipProps}
                            icon={<FolderIcon />}
                            size="small"
                          />
                        );
                      })
                    }
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      fullWidth
                      label={selectedLibraries.length === 1 ? `Path in ${selectedLibraries[0].library_name}` : "Folder Path"}
                      variant="outlined"
                      value={folderFilter}
                      onChange={(e) => setFolderFilter(e.target.value)}
                      placeholder="/folder/subfolder"
                      InputProps={{
                        startAdornment: (
                          <>
                            {selectedLibraries.length === 1 && (
                              <Typography 
                                variant="body2" 
                                component="span" 
                                sx={{ 
                                  mr: 0.5, 
                                  color: theme.palette.text.secondary,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '120px', 
                                  display: 'inline-block',
                                  verticalAlign: 'middle'
                                }}
                              >
                                {selectedLibraries[0].library_name}:
                              </Typography>
                            )}
                            <FolderIcon sx={{ color: 'action.active', mr: 1, verticalAlign: 'middle' }} />
                          </>
                        )
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                    <Button
                      variant="outlined"
                      sx={{ height: '56px' }} // Adjust height to match TextField
                      onClick={() => {
                        if (selectedLibraries.length === 1) {
                          setIsFolderBrowserOpen(true);
                        } else {
                          // Optionally, show a message that a single library must be selected
                          setError('Please select a single document library to browse its folders.');
                        }
                      }}
                      disabled={selectedLibraries.length !== 1} // Disable if not exactly one library is selected
                    >
                      Browse
                    </Button>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Filename/Pattern"
                    variant="outlined"
                    value={filenameFilter}
                    onChange={(e) => setFilenameFilter(e.target.value)}
                    placeholder="*.pdf or document.docx"
                    InputProps={{
                      startAdornment: <InsertDriveFileIcon sx={{ color: 'action.active', mr: 1 }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    multiple
                    options={users}
                    getOptionLabel={(option) => option.display_name}
                    value={selectedUsers}
                    onChange={(event, newValue) => {
                      setSelectedUsers(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Users/Groups"
                        placeholder="Select users or groups"
                        variant="outlined"
                      />
                    )}
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.user_id}>
                          {option.is_group ? (
                            <GroupIcon sx={{ color: 'action.active', mr: 1 }} />
                          ) : (
                            <PersonIcon sx={{ color: 'action.active', mr: 1 }} />
                          )}
                          {option.display_name}
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const tagProps = getTagProps({ index });
                        // Remove key from props that will be spread
                        const { key, ...chipProps } = tagProps;
                        return (
                          <Chip
                            key={option.user_id}
                            label={option.display_name}
                            {...chipProps}
                            icon={option.is_group ? <GroupIcon /> : <PersonIcon />}
                            size="small"
                          />
                        );
                      })
                    }
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSearch}
                      disabled={loading || selectedLibraries.length === 0}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    >
                      {loading ? 'Searching...' : 'Search'}
                    </Button>
                    
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        setSelectedLibraries([]);
                        setFolderFilter('');
                        setFilenameFilter('');
                        setSelectedUsers([]);
                        setSearchPerformed(false);
                        setPermissionData([]);
                      }}
                    >
                      Reset Filters
                    </Button>
                    
                    {searchPerformed && permissionData.length > 0 && (
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExport}
                      >
                        Export Results
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
            
            {searchPerformed && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Search Results
                    <Chip 
                      label={`${permissionData.length} items`} 
                      color="primary" 
                      size="small" 
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </Box>
                
                {permissionData.length === 0 ? (
                  <Alert severity="info">No matching items found.</Alert>
                ) : (
                  <>
                    {displayedGroupedPermissions.map((group, idx) => (
                      <Accordion key={idx} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            {group.itemInfo.type === 'Folder' ? (
                              <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                            ) : (
                              <InsertDriveFileIcon sx={{ mr: 1, color: 'info.main' }} />
                            )}
                            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                              {group.itemInfo.name}
                              <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                                ({group.itemInfo.path})
                              </Typography>
                            </Typography>
                            <Chip 
                              label={`${group.permissions.length} permissions`} 
                              size="small" 
                              color="default" 
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>User/Group</TableCell>
                                  <TableCell>Permission Level</TableCell>
                                  <TableCell>Inherited</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {group.permissions.map((perm, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{perm.user}</TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={stripPermissionPrefix(perm.permission)} 
                                        size="small"
                                        sx={{ 
                                          backgroundColor: getPermissionColor(perm.permission),
                                          color: theme.palette.getContrastText(getPermissionColor(perm.permission))
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>{perm.inherited ? 'Yes' : 'No'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {group.itemInfo.url && (
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="text"
                                size="small"
                                href={group.itemInfo.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Open in SharePoint
                              </Button>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                    
                    <TablePagination
                      component="div"
                      count={groupedPermissions.length}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                  </>
                )}
              </Paper>
            )}
          </>
        )}
        
        {viewMode === 'comparison' && (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                <FilterListIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Peer Comparison
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Compare permissions between two users or groups across selected document libraries.
              </Typography>
              
              <Grid container spacing={3}>
                {/* Library Selection */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={libraries}
                    getOptionLabel={(option) => option.library_name}
                    value={selectedLibraries}
                    onChange={(event, newValue) => {
                      setSelectedLibraries(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Document Libraries"
                        placeholder="Select document libraries"
                        variant="outlined"
                        disabled={loadingLibraries}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: loadingLibraries ? (
                            <CircularProgress size={20} />
                          ) : (
                            <>
                              <FolderIcon sx={{ color: 'action.active', mr: 1 }} />
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const tagProps = getTagProps({ index });
                        // Remove key from props that will be spread
                        const { key, ...chipProps } = tagProps;
                        return (
                          <Chip
                            key={option.library_id}
                            label={option.library_name}
                            {...chipProps}
                            icon={<FolderIcon />}
                            size="small"
                          />
                        );
                      })
                    }
                  />
                </Grid>
                
                {/* Folder Path and Filename Filters */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      fullWidth
                      label={selectedLibraries.length === 1 ? `Path in ${selectedLibraries[0].library_name}` : "Folder Path"}
                      variant="outlined"
                      value={folderFilter}
                      onChange={(e) => setFolderFilter(e.target.value)}
                      placeholder="/folder/subfolder"
                      InputProps={{
                        startAdornment: (
                          <>
                            {selectedLibraries.length === 1 && (
                              <Typography 
                                variant="body2" 
                                component="span" 
                                sx={{ 
                                  mr: 0.5, 
                                  color: theme.palette.text.secondary,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '120px',
                                  display: 'inline-block',
                                  verticalAlign: 'middle'
                                }}
                              >
                                {selectedLibraries[0].library_name}:
                              </Typography>
                            )}
                            <FolderIcon sx={{ color: 'action.active', mr: 1, verticalAlign: 'middle' }} />
                          </>
                        )
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                    <Button
                      variant="outlined"
                      sx={{ height: '56px' }} // Adjust height to match TextField
                      onClick={() => {
                        if (selectedLibraries.length === 1) {
                          setIsFolderBrowserOpen(true);
                        } else {
                          setError('Please select a single document library to browse its folders.');
                        }
                      }}
                      disabled={selectedLibraries.length !== 1}
                    >
                      Browse
                    </Button>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Filename/Pattern"
                    variant="outlined"
                    value={filenameFilter}
                    onChange={(e) => setFilenameFilter(e.target.value)}
                    placeholder="*.pdf or document.docx"
                    InputProps={{
                      startAdornment: <InsertDriveFileIcon sx={{ color: 'action.active', mr: 1 }} />
                    }}
                  />
                </Grid>
                
                {/* User A Selection */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => option.display_name}
                    value={userA}
                    onChange={(event, newValue) => {
                      setUserA(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="User/Group A"
                        placeholder="Select first user or group"
                        variant="outlined"
                        error={comparisonLoading && !userA}
                        helperText={comparisonLoading && !userA ? "Required for comparison" : ""}
                      />
                    )}
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.user_id}>
                          {option.is_group ? (
                            <GroupIcon sx={{ color: 'action.active', mr: 1 }} />
                          ) : (
                            <PersonIcon sx={{ color: 'action.active', mr: 1 }} />
                          )}
                          {option.display_name}
                        </li>
                      );
                    }}
                  />
                </Grid>
                
                {/* User B Selection */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => option.display_name}
                    value={userB}
                    onChange={(event, newValue) => {
                      setUserB(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="User/Group B"
                        placeholder="Select second user or group"
                        variant="outlined"
                        error={comparisonLoading && !userB}
                        helperText={comparisonLoading && !userB ? "Required for comparison" : ""}
                      />
                    )}
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.user_id}>
                          {option.is_group ? (
                            <GroupIcon sx={{ color: 'action.active', mr: 1 }} />
                          ) : (
                            <PersonIcon sx={{ color: 'action.active', mr: 1 }} />
                          )}
                          {option.display_name}
                        </li>
                      );
                    }}
                  />
                </Grid>
                
                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleComparisonSearch}
                      disabled={comparisonLoading || !userA || !userB || selectedLibraries.length === 0}
                      startIcon={comparisonLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    >
                      {comparisonLoading ? 'Comparing...' : 'Compare Permissions'}
                    </Button>
                    
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        setSelectedLibraries([]);
                        setFolderFilter('');
                        setFilenameFilter('');
                        setUserA(null);
                        setUserB(null);
                        setComparisonData([]);
                        setComparisonTab(0);
                      }}
                    >
                      Reset Filters
                    </Button>
                    
                    {comparisonData.length > 0 && (
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleComparisonExport}
                      >
                        Export Comparison
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
            
            {viewMode === 'comparison' && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Comparison Results
                    <Chip 
                      label={comparisonLoading ? 'Loading...' : `${totalItems} items`} 
                      color="primary" 
                      size="small" 
                      sx={{ ml: 1 }}
                      icon={comparisonLoading ? <CircularProgress size={14} color="inherit" /> : null}
                    />
                  </Typography>
                </Box>
                
                {warning && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {warning}
                  </Alert>
                )}
                
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                
                <Tabs value={comparisonTab} onChange={handleComparisonTabChange}>
                  <Tab label="All Items" disabled={comparisonLoading} />
                  <Tab label="Shared Permissions" disabled={comparisonLoading} />
                  <Tab label={`Unique to ${userA?.display_name || 'User A'}`} disabled={comparisonLoading} />
                  <Tab label={`Unique to ${userB?.display_name || 'User B'}`} disabled={comparisonLoading} />
                  <Tab label="Different Permission Levels" disabled={comparisonLoading} />
                </Tabs>
                
                {comparisonLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      Loading filter results...
                    </Typography>
                  </Box>
                ) : (!comparisonData || comparisonData.length === 0) ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {comparisonTab === 0 && "No items were found in the selected libraries."}
                    {comparisonTab === 1 && `No items found where ${userA?.display_name || 'User A'} and ${userB?.display_name || 'User B'} have the same permissions.`}
                    {comparisonTab === 2 && `No items found where only ${userA?.display_name || 'User A'} has permissions.`}
                    {comparisonTab === 3 && `No items found where only ${userB?.display_name || 'User B'} has permissions.`}
                    {comparisonTab === 4 && `No items found where ${userA?.display_name || 'User A'} and ${userB?.display_name || 'User B'} have different permission levels.`}
                  </Alert>
                ) : (
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item Name</TableCell>
                          <TableCell>Item Type</TableCell>
                          <TableCell>Path</TableCell>
                          <TableCell>{userA?.display_name || 'User A'} Permission</TableCell>
                          <TableCell>{userB?.display_name || 'User B'} Permission</TableCell>
                          <TableCell>Difference</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comparisonData.map((item, index) => (
                          <TableRow 
                            key={index}
                            sx={{ 
                              backgroundColor: getComparisonRowColor(item.permissionA, item.permissionB)
                            }}
                          >
                            <TableCell>
                              {item.item_url ? (
                                <Link href={item.item_url} target="_blank" rel="noopener noreferrer">
                                  {item.itemName}
                                </Link>
                              ) : (
                                item.itemName
                              )}
                            </TableCell>
                            <TableCell>
                              {item.item_type === 'Folder' ? (
                                <Chip icon={<FolderIcon />} label="Folder" size="small" />
                              ) : (
                                <Chip icon={<InsertDriveFileIcon />} label="File" size="small" />
                              )}
                            </TableCell>
                            <TableCell>{item.item_path}</TableCell>
                            <TableCell>
                              {item.permissionA ? (
                                <Chip 
                                  label={stripPermissionPrefix(item.permissionA)} 
                                  size="small"
                                  sx={{ 
                                    backgroundColor: getPermissionColor(item.permissionA),
                                    color: theme.palette.getContrastText(getPermissionColor(item.permissionA))
                                  }}
                                />
                              ) : (
                                <Chip label="No Access" size="small" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell>
                              {item.permissionB ? (
                                <Chip 
                                  label={stripPermissionPrefix(item.permissionB)} 
                                  size="small"
                                  sx={{ 
                                    backgroundColor: getPermissionColor(item.permissionB),
                                    color: theme.palette.getContrastText(getPermissionColor(item.permissionB))
                                  }}
                                />
                              ) : (
                                <Chip label="No Access" size="small" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell>{getPermissionDifference(item.permissionA, item.permissionB)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                
                <Box sx={{ mt: 2 }}>
                  <TablePagination
                    component="div"
                    count={totalItems}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
                    disabled={comparisonLoading}
                  />
                </Box>
              </Paper>
            )}
          </>
        )}
      </Box>

      {selectedLibraries.length === 1 && (
        <FolderBrowserDialog
          open={isFolderBrowserOpen}
          onClose={() => setIsFolderBrowserOpen(false)}
          onSelectPath={(selectedPath) => {
            // Strip the drive and library prefix to get a relative path
            const libName = selectedLibraries[0].library_name;
            const prefixRegex = new RegExp(`^/drives/[^/]+/${libName}`, 'i');
            let relPath = selectedPath.replace(prefixRegex, '').trim();
            // Normalize resulting path
            if (!relPath || relPath === '/') {
              relPath = '';
            } else if (!relPath.startsWith('/')) {
              relPath = '/' + relPath;
            }
            setFolderFilter(relPath);
            setIsFolderBrowserOpen(false);
          }}
          selectedLibrary={selectedLibraries[0]} // Pass the single selected library
          initialPath={folderFilter}
          configId={effectiveConfigId} // Pass the effectiveConfigId (configId)
          getToken={() => authApi.getToken()}
          userEmail={userSession?.email}
        />
      )}
    </Box>
  );
};

export default SecurityLookup; 